/**
 * Планы разделов «Звонки» / «Встречи».
 *
 * Лента и карточка не привязаны к одному плану: результат — слияние последних
 * успешных разборов планов раздела (`communicationKind` в запросе ленты,
 * `sectionResult` для `/result`); разборы планами другого раздела не участвуют.
 * Отсюда же — объединённая схема для колонок/поиска и значения фильтра
 * «Тип коммуникации» (enum поля `callType` в схемах планов).
 */
import type {
  PlanCommunicationKind,
  PlanSchemaProperty,
  WorkspacePlan,
} from './types';

export const PLAN_KIND_LABELS: Record<PlanCommunicationKind, string> = {
  call: 'Звонки',
  meeting: 'Встречи',
};

/** Поле результата с типом коммуникации (значения перечислены в плане). */
export const COMMUNICATION_TYPE_FIELD = 'callType';

/** Планы раздела. Нет планов этого типа — пустой список (разделы не смешиваются). */
export const plansForKind = (
  plans: WorkspacePlan[],
  kind: PlanCommunicationKind,
): WorkspacePlan[] => plans.filter((p) => p.communicationKind === kind);

/**
 * Активные планы раздела — по ним строятся колонки, поиск и фильтр «Тип
 * коммуникации» таблиц «Звонки»/«Встречи»: черновик или архивный план не меняет
 * рабочую таблицу.
 */
export const activePlansForKind = (
  plans: WorkspacePlan[],
  kind: PlanCommunicationKind,
): WorkspacePlan[] =>
  plansForKind(plans, kind).filter((p) => p.state === 'active');

/**
 * Объединённые `schema.properties` планов. При совпадении имён описание поля
 * (подпись, тип, «в таблице») берётся из последнего плана списка; порядок —
 * по первому появлению поля (список с бэка: базовый план, далее по дате создания).
 */
export const mergePlanProperties = (
  plans: WorkspacePlan[],
): Record<string, PlanSchemaProperty> => {
  const merged: Record<string, PlanSchemaProperty> = {};
  for (const plan of plans) {
    const props = (plan.schema?.['properties'] ?? {}) as Record<
      string,
      PlanSchemaProperty
    >;
    for (const [name, prop] of Object.entries(props)) merged[name] = prop;
  }
  return merged;
};

/** Ответ `/result`: результат по планам + их слияние (поздний план перекрывает). */
interface PlanResultsResponse {
  results?: unknown[];
  formResult?: Record<string, unknown> | null;
}

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Результат коммуникации для раздела (как строка ленты с `communicationKind`):
 * только поля, которые дали планы раздела. Разбор планом другого раздела
 * (напр. базовым планом звонков с пустым правилом) не подмешивается — у встречи
 * не появится «Холодный звонок». Если значение `formResult` (слияние всех планов,
 * поздний перекрывает) совпадает с одним из планов раздела — берём его: он самый
 * поздний.
 */
export const sectionResult = (
  response: PlanResultsResponse | null | undefined,
  plans: WorkspacePlan[],
  kind: PlanCommunicationKind,
): Record<string, unknown> => {
  const latest = response?.formResult ?? {};
  const merged: Record<string, unknown> = {};
  const kindSids = new Set(
    plansForKind(plans, kind).map((p) => p.sid.toLowerCase()),
  );
  const kindValues: Record<string, unknown[]> = {};
  for (const entry of response?.results ?? []) {
    const {planId, formResult} = (entry ?? {}) as {
      planId?: unknown;
      formResult?: unknown;
    };
    if (!kindSids.has(String(planId).toLowerCase())) continue;
    if (!formResult || typeof formResult !== 'object') continue;
    for (const [key, value] of Object.entries(formResult)) {
      (kindValues[key] ??= []).push(value);
    }
  }
  for (const [key, values] of Object.entries(kindValues)) {
    merged[key] = values.some((v) => sameValue(v, latest[key]))
      ? latest[key]
      : values[0];
  }
  return merged;
};

/** Числовой критерий результата (шкала 0–10). */
export interface ResultCriterion {
  key: string;
  label: string;
  value: number;
}

/**
 * Критерии результата — числовые поля 0–10 (кроме итогового `score`), как в
 * карточке звонка; подписи — `title` из схем планов (`labels`), иначе ключ.
 */
export const resultCriteria = (
  result: Record<string, unknown> | null | undefined,
  labels: Record<string, PlanSchemaProperty> = {},
): ResultCriterion[] =>
  Object.entries(result ?? {})
    .filter(
      ([key, value]) =>
        key !== 'score' &&
        typeof value === 'number' &&
        value >= 0 &&
        value <= 10,
    )
    .map(([key, value]) => ({
      key,
      label: labels[key]?.title ?? key,
      value: value as number,
    }));

/** Оценка /10: поле `score` результата, иначе среднее критериев; null — оценок нет. */
export const resultScore = (
  result: Record<string, unknown> | null | undefined,
): number | null => {
  if (typeof result?.['score'] === 'number') return result['score'];
  const criteria = resultCriteria(result);
  if (criteria.length === 0) return null;
  return criteria.reduce((sum, c) => sum + c.value, 0) / criteria.length;
};

/** Значения фильтра «Тип коммуникации» — enum поля `callType` планов, без дублей. */
export const communicationTypeOptions = (plans: WorkspacePlan[]): string[] => {
  const values = new Set<string>();
  for (const plan of plans) {
    const props = (plan.schema?.['properties'] ?? {}) as Record<
      string,
      PlanSchemaProperty
    >;
    const options = props[COMMUNICATION_TYPE_FIELD]?.enum ?? [];
    for (const v of options) {
      if (typeof v === 'string' && v.trim()) values.add(v);
    }
  }
  return [...values];
};
